import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Divider,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AssessmentIcon from '@mui/icons-material/Assessment';

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "100%",
  padding: theme.spacing(2)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(15, 40, 76, 0.1)',
    transform: 'translateY(-1px)',
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(15, 40, 76, 0.15)',
  },
}));

interface CampaignMetrics {
  id: string;
  campaignName: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  conversions: number;
  revenue: number;
  sendDate: string;
}

interface PerformanceMetric {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
}

const MeasureTab: React.FC = () => {
  const selectedDealer = useSelector(selectDealerSelected);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('30days');
  
  // Sample performance metrics
  const [performanceMetrics] = useState<PerformanceMetric[]>([
    { label: 'Total Emails Sent', value: '12,450', change: '+8.2%', trend: 'up' },
    { label: 'Open Rate', value: '68.5%', change: '+2.1%', trend: 'up' },
    { label: 'Click Rate', value: '15.3%', change: '-0.8%', trend: 'down' },
    { label: 'Conversion Rate', value: '4.7%', change: '+1.2%', trend: 'up' },
    { label: 'Revenue Generated', value: '$89,250', change: '+12.5%', trend: 'up' },
    { label: 'ROI', value: '450%', change: '+18.3%', trend: 'up' }
  ]);
  
  // Sample campaign data
  const [campaignData] = useState<CampaignMetrics[]>([
    {
      id: '1',
      campaignName: 'Lease Renewal Campaign',
      sent: 1250,
      delivered: 1198,
      opened: 821,
      clicked: 156,
      conversions: 23,
      revenue: 28750,
      sendDate: '2024-01-10'
    },
    {
      id: '2',
      campaignName: 'Service Reminder',
      sent: 890,
      delivered: 867,
      opened: 625,
      clicked: 162,
      conversions: 31,
      revenue: 15500,
      sendDate: '2024-01-08'
    },
    {
      id: '3',
      campaignName: 'New Model Announcement',
      sent: 5420,
      delivered: 5201,
      opened: 2351,
      clicked: 423,
      conversions: 67,
      revenue: 45000,
      sendDate: '2024-01-05'
    }
  ]);

  const handleDateRangeChange = useCallback((newRange: string) => {
    setDateRange(newRange);
    setSuccessMessage(`Date range updated to ${newRange === '30days' ? '30 days' : newRange === '7days' ? '7 days' : '90 days'}`);
  }, []);

  const handleExportReport = useCallback(() => {
    try {
      setLoading(true);
      
      // Simulate export process
      setTimeout(() => {
        setSuccessMessage('Performance report exported successfully!');
        setLoading(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report. Please try again.');
      setLoading(false);
    }
  }, []);

  const handleScheduleReport = useCallback(() => {
    setSuccessMessage('Report scheduling will be available in the next update.');
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const calculateRate = (numerator: number, denominator: number): string => {
    return denominator > 0 ? ((numerator / denominator) * 100).toFixed(1) + '%' : '0%';
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      default: return '➡️';
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up': return 'success.main';
      case 'down': return 'error.main';
      default: return 'text.secondary';
    }
  };

  return (
    <MainContainer>
      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AnalyticsIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.primary.main
              }}
            >
              Measure Page
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              Track campaign performance and enable data queries
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => setSuccessMessage('Ask MIA chat interface will be available in the next update.')}
            sx={{ 
              minWidth: 140,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Ask MIA
          </Button>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => handleDateRangeChange(e.target.value)}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            color="primary"
            onClick={handleScheduleReport}
            sx={{ 
              minWidth: 140,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Save Query
          </Button>
        </Box>
      </Paper>

      {/* Key Capabilities */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          mb: 3
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main,
            mb: 2
          }}
        >
          Key Capabilities
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Current customer portal view with Ask MIA button
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                On clicking Ask Mia, it opens chat interface with pre-configured top questions. User can also enter their own queries
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Pre-configured top questions will always have data readily available
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Users can save queries and schedule updates. For example, email me the answers to these five questions every Monday morning at 9 AM for my staff meeting.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Performance Metrics */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AssessmentIcon 
          sx={{ 
            color: theme.palette.primary.main,
            fontSize: 24 
          }} 
        />
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          Key Performance Metrics
        </Typography>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {performanceMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <MetricCard>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, fontSize: '2rem' }}>
                {metric.value}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.95, fontSize: '0.85rem' }}>
                {metric.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontSize: '0.8rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {getTrendIcon(metric.trend)} {metric.change}
                </Typography>
              </Box>
            </MetricCard>
          </Grid>
        ))}
      </Grid>

      {/* Campaign Performance Table */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <AssessmentIcon 
              sx={{ 
                color: theme.palette.primary.main,
                fontSize: 24 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Campaign Performance Details
            </Typography>
          </Box>
          
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  '& th': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }
                }}>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="center">Sent</TableCell>
                  <TableCell align="center">Delivered</TableCell>
                  <TableCell align="center">Open Rate</TableCell>
                  <TableCell align="center">Click Rate</TableCell>
                  <TableCell align="center">Conversions</TableCell>
                  <TableCell align="center">Revenue</TableCell>
                  <TableCell align="center">Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignData.map((campaign) => (
                  <TableRow 
                    key={campaign.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {campaign.campaignName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {campaign.sent.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.delivered.toLocaleString()}
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({calculateRate(campaign.delivered, campaign.sent)})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {campaign.opened.toLocaleString()}
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({calculateRate(campaign.opened, campaign.delivered)})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {campaign.clicked.toLocaleString()}
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({calculateRate(campaign.clicked, campaign.opened)})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={campaign.conversions.toString()} 
                        size="small" 
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({calculateRate(campaign.conversions, campaign.clicked)})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        ${campaign.revenue.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {new Date(campaign.sendDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>

      {/* Performance Insights */}
      <Paper 
        elevation={0}
        sx={{ 
          mt: 3, 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <TipsAndUpdatesIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 24 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.primary.main
            }}
          >
            Performance Insights & Recommendations
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
                📊 High Engagement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Service reminder campaigns show 18.7% click rates - consider expanding this strategy.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
                🎯 Optimization Opportunity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Lease renewal campaigns have room for improvement in open rates with better subject lines.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
                💰 Revenue Impact
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Your campaigns generated $89,250 in revenue this month, exceeding targets by 12.5%.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
                ⏰ Best Send Times
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Tuesday and Thursday 10 AM show highest open rates for your audience.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default MeasureTab;
